import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Plus,
  Mic,
  Bot,
  ChevronDown,
  MessageCircle,
  Paperclip,
  X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import PageBreadcrumb from '../common/PageBreadcrumb';
import AIService from '../../services/aiService';
import { useAuth } from '../../context/AuthContext';

const SUGGESTED_PROMPTS = [
  'Draft a high-level RFP outline for IT managed services',
  'Write evaluation criteria and weighting for a construction vendor RFP',
  'List RFP sections and key risk clauses for cloud software procurement',
  'Create due diligence and security questions for vendor questionnaires',
  'Propose a timeline and submission format for a competitive RFP',
  'Refine this scope: annual facilities maintenance and janitorial',
];

const markdownComponents = {
  h1: ({ children }) => (
    <h1 style={{ fontSize: '18px', fontWeight: '700', margin: '8px 0 4px 0', color: '#1f2937' }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: '16px', fontWeight: '700', margin: '6px 0 3px 0', color: '#1f2937' }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: '15px', fontWeight: '700', margin: '4px 0 2px 0', color: '#1f2937' }}>{children}</h3>
  ),
  p: ({ children }) => <p style={{ margin: '4px 0', lineHeight: 1.6 }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '18px' }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '18px' }}>{children}</ol>,
  li: ({ children }) => <li style={{ margin: '2px 0', lineHeight: 1.5 }}>{children}</li>,
  strong: ({ children }) => <strong style={{ fontWeight: '700', color: '#1f2937' }}>{children}</strong>,
  code: ({ children }) => (
    <code
      style={{
        backgroundColor: '#f3f4f6',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '13px',
        fontFamily: 'ui-monospace, monospace',
      }}
    >
      {children}
    </code>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: '12px', margin: '8px 0', color: '#6b7280' }}>
      {children}
    </blockquote>
  ),
};

const RFPDrafting = () => {
  const { user } = useAuth();
  const displayName =
    (user && (user.firstName || user.name)) ||
    (user && user.email && user.email.split('@')[0]) ||
    'there';

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [rfpSessionId, setRfpSessionId] = useState(null);
  const [rfpOptions, setRfpOptions] = useState([]);
  const [rfpFieldKey, setRfpFieldKey] = useState(null);
  const [rfpHasOptions, setRfpHasOptions] = useState(false);
  const [rfpProgress, setRfpProgress] = useState({ filled: 0, totalRequired: 0 });
  const [rfpStatus, setRfpStatus] = useState(null); // 'needs_input' | 'ready' | null
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const visibleChips = showAllChips ? SUGGESTED_PROMPTS : SUGGESTED_PROMPTS.slice(0, 3);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [inputValue]);

  function processRfpResponse(data) {
    if (data.status === 'needs_input') {
      setRfpStatus('needs_input');
      setRfpHasOptions(data.hasOptions || false);
      setRfpOptions(data.options || []);
      setRfpFieldKey(data.fieldKey || null);
      if (data.progress) setRfpProgress(data.progress);
    }
    if (data.status === 'ready') {
      setRfpStatus('ready');
      setRfpHasOptions(false);
      setRfpOptions([]);
      setRfpFieldKey(null);
      if (data.progress) setRfpProgress(data.progress);
    }
  }

  const runAssistant = async (userText) => {
    if (!userText.trim()) return;
    setIsLoading(true);
    setIsTyping(true);
    try {
      // Ensure backend RFP session is created once.
      let currentSessionId = rfpSessionId;
      if (!currentSessionId) {
        const started = await AIService.startRfpSession('it_surveillance');
        currentSessionId = started.sessionId;
        setRfpSessionId(currentSessionId);
      }

      // Upload selected files first so backend can extract context.
      for (const file of attachments) {
        const uploadData = await AIService.uploadRfpDocument(currentSessionId, file);
        processRfpResponse(uploadData);
      }

      const data = await AIService.rfpDraftingChat(currentSessionId, userText);
      processRfpResponse(data);
      const text = data.question || data.message || data.response ||
        "I could not generate a response. Please check your connection and try again.";
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: text,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      if (attachments.length > 0) {
        setAttachments([]);
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: `**Something went wrong.** ${e.message || 'Please try again.'}`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleSend = async () => {
    const t = inputValue.trim();
    if (!t || isLoading) return;
    const userMsg = {
      id: `u-${Date.now()}`,
      type: 'user',
      content: t,
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    await runAssistant(t);
  };

  const sendWithText = async (text) => {
    const t = (text || '').trim();
    if (!t || isLoading) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `u-${Date.now()}`,
        type: 'user',
        content: t,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
    setInputValue('');
    await runAssistant(t);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const applyChip = (text) => {
    sendWithText(text);
  };

  const newChat = () => {
    setMessages([]);
    setInputValue('');
    setAttachments([]);
    setRfpSessionId(null);
    setRfpOptions([]);
    setRfpFieldKey(null);
    setRfpHasOptions(false);
    setRfpProgress({ filled: 0, totalRequired: 0 });
    setRfpStatus(null);
  };

  const handleOptionClick = async (option) => {
    if (!rfpSessionId || !rfpFieldKey || isLoading) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `u-${Date.now()}`,
        type: 'user',
        content: option,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
    setRfpHasOptions(false);
    setRfpOptions([]);
    setIsLoading(true);
    setIsTyping(true);
    try {
      const data = await AIService.answerRfpQuestion(rfpSessionId, rfpFieldKey, option);
      processRfpResponse(data);
      const text = data.question || data.message || data.response || 'Response received.';
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: text,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: `**Something went wrong.** ${e.message || 'Please try again.'}`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleGenerateRfp = async () => {
    if (!rfpSessionId || isLoading) return;
    setIsLoading(true);
    try {
      await AIService.generateRfpDoc(rfpSessionId);
      setRfpStatus(null);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: `**RFP generation failed.** ${e.message || 'Please try again.'}`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event) => {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) return;
    const allowed = new Set([
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ]);
    const filtered = picked.filter((f) => {
      const lower = f.name.toLowerCase();
      return (
        allowed.has(f.type) ||
        lower.endsWith('.txt') ||
        lower.endsWith('.pdf') ||
        lower.endsWith('.doc') ||
        lower.endsWith('.docx')
      );
    });
    if (!filtered.length) return;
    setAttachments((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
      const merged = [...prev];
      filtered.forEach((f) => {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        if (!existing.has(key)) merged.push(f);
      });
      return merged;
    });
    event.target.value = '';
  };

  const removeAttachment = (fileToRemove) => {
    setAttachments((prev) =>
      prev.filter(
        (f) =>
          !(
            f.name === fileToRemove.name &&
            f.size === fileToRemove.size &&
            f.lastModified === fileToRemove.lastModified
          )
      )
    );
  };

  return (
    <>
      <PageBreadcrumb />
      <div
        className="page-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 220px)',
          fontFamily: 'var(--font-family)',
        }}
      >
        {messages.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              maxWidth: '800px',
              width: '100%',
              margin: '0 auto 12px',
            }}
          >
            <button
              type="button"
              className="btn-modern btn-modern-outline"
              onClick={newChat}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <MessageCircle size={16} />
              New conversation
            </button>
          </div>
        )}

        {messages.length > 0 && (
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              maxWidth: '800px',
              width: '100%',
              margin: '0 auto 16px',
              padding: '0 4px 8px',
            }}
          >
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: m.type === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '16px',
                }}
              >
                {m.type === 'user' ? (
                  <div
                    style={{
                      maxWidth: '85%',
                      padding: '12px 16px',
                      background: '#f3f4f6',
                      color: '#111827',
                      borderRadius: '16px 16px 4px 16px',
                      fontSize: '14px',
                      lineHeight: 1.5,
                    }}
                  >
                    {m.content}
                    <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '6px', textAlign: 'right' }}>
                      {m.timestamp}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      maxWidth: '90%',
                      padding: '14px 18px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '16px 16px 16px 4px',
                      fontSize: '14px',
                      lineHeight: 1.55,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '-10px',
                        left: '10px',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: 'var(--sidebar-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Bot size={14} style={{ color: '#fff' }} />
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <ReactMarkdown components={markdownComponents}>{m.content}</ReactMarkdown>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '10px' }}>{m.timestamp}</div>
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '12px 16px',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '16px',
                    fontSize: '13px',
                    color: '#6b7280',
                  }}
                >
                  Drafting response…
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {messages.length === 0 && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '16px 12px 24px',
              maxWidth: '720px',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <p
              style={{
                margin: '0 0 8px 0',
                fontSize: '16px',
                fontWeight: 500,
                color: '#2563eb',
                fontFamily: 'var(--font-family)',
              }}
            >
              Hi, {displayName}
            </p>
            <h2
              style={{
                margin: '0 0 28px 0',
                fontSize: '28px',
                fontWeight: 600,
                color: '#111827',
                lineHeight: 1.2,
                fontFamily: 'var(--font-family)',
              }}
            >
              Where should we start?
            </h2>
          </div>
        )}

        <div
          style={{
            maxWidth: '720px',
            width: '100%',
            margin: '0 auto',
            padding: messages.length > 0 ? '0 4px 24px' : '0 4px 32px',
            marginTop: messages.length > 0 ? 0 : 'auto',
          }}
        >
          {rfpProgress.totalRequired > 0 && (
            <div
              style={{
                maxWidth: '720px',
                margin: '0 auto 12px',
                padding: '10px 12px',
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
              }}
            >
              <div style={{ fontSize: '13px', color: '#334155', marginBottom: '6px' }}>
                Fields collected: {rfpProgress.filled} / {rfpProgress.totalRequired}
              </div>
              <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '999px' }}>
                <div
                  style={{
                    height: '8px',
                    borderRadius: '999px',
                    background: 'var(--primary-color)',
                    width: `${Math.min(
                      100,
                      Math.round((rfpProgress.filled / Math.max(rfpProgress.totalRequired, 1)) * 100),
                    )}%`,
                    transition: 'width 0.2s ease',
                  }}
                />
              </div>
            </div>
          )}

          {rfpHasOptions && rfpOptions.length > 0 && (
            <div style={{ maxWidth: '720px', margin: '0 auto 12px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {rfpOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleOptionClick(option)}
                    className="btn-modern btn-modern-outline"
                    style={{ fontSize: '13px', padding: '6px 12px' }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {rfpStatus === 'ready' && (
            <div style={{ maxWidth: '720px', margin: '0 auto 12px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-modern btn-modern-primary"
                onClick={handleGenerateRfp}
                disabled={isLoading || !rfpSessionId}
              >
                Generate RFP Document
              </button>
            </div>
          )}

          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)',
              border: '1px solid #eef0f4',
              padding: '16px 16px 12px',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.pdf,.doc,.docx,text/plain,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <textarea
              ref={textareaRef}
              rows={3}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              placeholder="Describe your RFP need, category, or paste an outline to refine…"
              style={{
                width: '100%',
                minHeight: '88px',
                border: 'none',
                resize: 'none',
                fontSize: '15px',
                fontFamily: 'var(--font-family)',
                color: '#111827',
                outline: 'none',
                background: 'transparent',
                lineHeight: 1.5,
              }}
            />
            {attachments.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '10px',
                }}
              >
                {attachments.map((file) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      borderRadius: '999px',
                      border: '1px solid #dbe1ea',
                      background: '#f8fafc',
                      maxWidth: '100%',
                    }}
                  >
                    <Paperclip size={13} style={{ color: '#64748b', flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#334155',
                        maxWidth: '260px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={file.name}
                    >
                      {file.name} ({formatFileSize(file.size)})
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(file)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#64748b',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '4px',
                paddingTop: '8px',
                borderTop: '1px solid #f3f4f6',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  title="Upload .txt, .pdf, .doc, .docx"
                  onClick={handleOpenFilePicker}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    background: '#fafafa',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#374151',
                  }}
                >
                  <Plus size={18} />
                </button>
                <button
                  type="button"
                  title="Voice (coming soon)"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    background: '#fafafa',
                    cursor: 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#374151',
                  }}
                >
                  <Mic size={18} />
                </button>
              </div>
              <button
                type="button"
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                style={{
                  minWidth: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'var(--sidebar-bg)',
                  color: '#fff',
                  cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                  opacity: inputValue.trim() && !isLoading ? 1 : 0.45,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(28, 35, 51, 0.2)',
                }}
                aria-label="Send"
              >
                <Send size={18} />
              </button>
            </div>
          </div>

          {messages.length === 0 && (
            <div style={{ marginTop: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  justifyContent: 'center',
                }}
              >
                {visibleChips.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => applyChip(label)}
                    style={{
                      padding: '8px 14px',
                      fontSize: '13px',
                      lineHeight: 1.4,
                      color: '#1e3a5f',
                      background: '#e0f2fe',
                      border: '1px solid #bae6fd',
                      borderRadius: '999px',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family)',
                      textAlign: 'left',
                      maxWidth: '100%',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {SUGGESTED_PROMPTS.length > 3 && (
                <button
                  type="button"
                  onClick={() => setShowAllChips((s) => !s)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    margin: '14px auto 0',
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    fontSize: '13px',
                    cursor: 'pointer',
                    width: '100%',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {showAllChips ? 'Show less' : 'Show more'}
                  <ChevronDown
                    size={16}
                    style={{ transform: showAllChips ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                  />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RFPDrafting;
